import { styled } from '@mui/material'
import React from 'react'

import { LanguageType } from '@/app/module/campaigns/helpers'
import { CampaignFileResult } from '@/app/module/campaigns/types'
import { wrapActions } from '@/app/module/campaigns/utils/actions/wrap'
import { BaseWhatsAppContent, WhatsAppTab } from '@/app/module/common/whatsapp/helpers'
import { FileUploadType } from '@/app/types'

import LanguageSelector from './language-selector'
import { defaultOnAnswerText, getWALanguageMap } from './types/multimessage/content'
import CampaignContentWhatsapp from './whatsapp'
import { WhatsAppMessageType, WhatsappProps } from './whatsapp-props'
import {
  getTranslatedContent,
  hasButtons,
  hasList,
  resetContentOnTabChange,
  shouldDisplayConfirmationMultilingual,
  toTranslation,
} from './whatsapp-utils'

type Props = WhatsappProps & {
  languages: LanguageType[]
}

const CampaignContentWhatsappMultilingual: React.FC<Props> = ({
  defaultLanguage,
  info,
  item,
  label,
  languages = [],
  tab,
  changeHandler,
  onTabChange,
  saveFileHandler,
  setPlaying,
  ...props
}) => {
  const [activeLanguage, setActiveLanguage] = React.useState<string>('')
  const [playing, setPlayingState] = React.useState(false)
  const [recording, setRecording] = React.useState(false)
  const [uploading, setUploading] = React.useState(false)

  const multilingual = languages?.length > 0

  const handleSetPlaying = React.useCallback(
    (playId: string) => {
      setPlayingState(!!playId)
      setPlaying(playId)
    },
    [setPlaying],
  )

  const handleStop = React.useCallback(() => setPlayingState(false), [])

  const handleContentChange = React.useCallback(
    (updatedItem: WhatsAppMessageType) => {
      if (!activeLanguage || activeLanguage === defaultLanguage) {
        changeHandler({
          ...updatedItem,
          message: {
            ...updatedItem.message,
            translations: item.message.translations,
          },
        })
        return
      }

      changeHandler({
        ...item,
        message: {
          ...item.message,
          translations: {
            ...item.message.translations,
            [activeLanguage]: toTranslation(updatedItem.message),
          },
          registerTemplate: updatedItem.message.registerTemplate,
        },
      })
    },
    [activeLanguage, defaultLanguage, item, changeHandler],
  )

  const handleTabChange = React.useCallback(
    (targetTab: WhatsAppTab) => {
      if (onTabChange) {
        onTabChange(targetTab)
      }

      const newItem = resetContentOnTabChange(targetTab, item)

      const hasWAButtons = hasButtons(newItem)
      const hasWAList = hasList(newItem)

      if (!newItem.hasQuestion || (!hasWAButtons && !hasWAList) || targetTab === WhatsAppTab.Text) {
        changeHandler(newItem)
        return
      }

      changeHandler({
        ...newItem,
        onAnswer: defaultOnAnswerText.map((answer) => ({
          ...answer,
          actions: wrapActions(answer.actions),
        })),
        onButtons: undefined,
        onList: undefined,
      })
    },
    [item, changeHandler, onTabChange],
  )

  const handleFileUpload = React.useCallback(
    async (file: FileUploadType): Promise<CampaignFileResult> => {
      setUploading(true)
      try {
        const result = await saveFileHandler(file)
        if (result instanceof Error) {
          return result
        }
        if (file.cancelled) {
          return new Error('Upload cancelled')
        }

        if (!activeLanguage || activeLanguage === defaultLanguage) {
          changeHandler({
            ...item,
            message: {
              ...item.message,
              [tab]: result.filename,
            },
          })
          return result
        }

        changeHandler({
          ...item,
          message: {
            ...item.message,
            translations: {
              ...item.message.translations,
              [activeLanguage]: toTranslation({
                ...item.message.translations?.[activeLanguage],
                [tab]: result.filename,
              }),
            },
          },
        })
        return result
      } finally {
        setUploading(false)
      }
    },
    [activeLanguage, defaultLanguage, item, tab, changeHandler, saveFileHandler],
  )

  const shouldConfirm = React.useCallback(
    (targetTab: WhatsAppTab, content: BaseWhatsAppContent) => {
      const hasButtonOrList = hasButtons(item) || hasList(item)

      return shouldDisplayConfirmationMultilingual(targetTab, content, item.message, hasButtonOrList, {
        activeLanguage,
        defaultLanguage,
      })
    },
    [activeLanguage, defaultLanguage, item],
  )

  const translatedItem = React.useMemo(
    () => ({
      ...item,
      message: getTranslatedContent(item, activeLanguage, defaultLanguage),
    }),
    [activeLanguage, defaultLanguage, item],
  )

  const langSelectDisabled = playing || recording || uploading

  const languageContentMap = React.useMemo(
    () => getWALanguageMap(item.message, defaultLanguage),
    [item, defaultLanguage],
  )

  return (
    <Container>
      {multilingual && (
        <LanguageSelectorContainer>
          <LanguageSelector
            activeLanguage={activeLanguage}
            defaultLanguage={defaultLanguage}
            disabled={langSelectDisabled}
            languageContentMap={languageContentMap}
            languages={languages}
            setActiveLanguage={setActiveLanguage}
          />
        </LanguageSelectorContainer>
      )}

      <CampaignContentWhatsapp
        {...props}
        defaultLanguage={defaultLanguage}
        info={info}
        item={translatedItem}
        label={label}
        tab={tab}
        uploading={uploading}
        changeHandler={handleContentChange}
        onRecordingChange={setRecording}
        onStop={handleStop}
        onTabChange={handleTabChange}
        saveFileHandler={handleFileUpload}
        setPlaying={handleSetPlaying}
        shouldConfirm={shouldConfirm}
      />
    </Container>
  )
}

const Container = styled('div')({
  position: 'relative',
})

const LanguageSelectorContainer = styled('div')({
  display: 'flex',
  justifyContent: 'flex-end',
  paddingTop: 8,
})

export default CampaignContentWhatsappMultilingual

export type { Props as WhatsappMultilingualProps }
